import React, { FC, useEffect, useState } from 'react';
import type { PopoverProps } from 'wix-style-react/dist/types/Popover';
import { marketplaceHeaderClientGenericClick } from '@wix/bi-logger-marketplace/v2';
import {
  Box,
  Layout,
  Cell,
  Popover,
  TextButton,
  TextButtonWeight,
} from 'wix-style-react';
import { ChevronDown } from '@wix/wix-ui-icons-common';
import { dataHooks, popoverIds } from '../utils/constants';
import { useBi } from '../../Hooks/useBi';
import {
  CurrencyOption,
  getTranslatedCurrencyOptions,
} from '../utils/Currency/formatCurrencyOptions';
import s from './CurrencySelector.module.scss';

interface CurrencySelectorTypeProps {
  placement: PopoverProps['placement'];
  moveBy: { x: number; y: number };
  showArrow: boolean;
  className: string;
  withSuffixIcon: boolean;
  triggerElementWeight: TextButtonWeight;
  enableClickTrigger: boolean;
  enableHoverTrigger: boolean;
}

export interface CurrencySelectorProps {
  value?: string;
  pageName?: string;
  onChange: (option: CurrencyOption) => void;
  localeDataTranslations: Record<string, any>;
  isLess1000View?: boolean;
  type?: 'default' | 'loggedIn' | 'notLoggedIn';
}

const defaultProps: CurrencySelectorTypeProps = {
  placement: 'bottom',
  moveBy: { x: -140, y: 30 },
  showArrow: true,
  className: '',
  withSuffixIcon: true,
  triggerElementWeight: 'normal',
  enableClickTrigger: true,
  enableHoverTrigger: false,
};

const defaultNotLoggedInProps: CurrencySelectorTypeProps = {
  placement: 'bottom',
  moveBy: { x: -40, y: 30 },
  showArrow: true,
  className: '',
  withSuffixIcon: true,
  triggerElementWeight: 'normal',
  enableClickTrigger: true,
  enableHoverTrigger: false,
};

const defaultLoggedInProps: CurrencySelectorTypeProps = {
  placement: 'left',
  moveBy: { x: 0, y: 34 },
  showArrow: false,
  className: 'menuLink',
  withSuffixIcon: false,
  triggerElementWeight: 'thin',
  enableClickTrigger: false,
  enableHoverTrigger: true,
};

const CurrencySelector: FC<CurrencySelectorProps> = ({
  value,
  pageName = '',
  onChange,
  localeDataTranslations,
  isLess1000View = false,
  type = 'default',
}) => {
  const [selection, setSelection] = useState<CurrencyOption>();
  const options = getTranslatedCurrencyOptions(localeDataTranslations);
  const [shown, setShown] = useState<boolean>(false);
  const logger = useBi();
  const popoverProps =
    type === 'default'
      ? defaultProps
      : type === 'loggedIn'
      ? defaultLoggedInProps
      : defaultNotLoggedInProps;

  useEffect(() => {
    const selectedOption = options.find((option) => option.id === value);
    setSelection(selectedOption);
  }, [value, setSelection]);

  useEffect(() => {
    if (isLess1000View) {
      setShown(false);
    }
  }, [isLess1000View]);

  const onSelected = (option: CurrencyOption) => {
    setSelection(option);
    onChange?.(option);
    setShown(false);
  };

  const OptionCell = ({ option }: { option: CurrencyOption }) => (
    <Cell span={12}>
      <TextButton
        as="button"
        skin="dark"
        dataHook={`sort-menu-item-${option.label}`}
        onClick={() => {
          logger.report(
            marketplaceHeaderClientGenericClick({
              clickType: 'change currency',
              click_data: JSON.stringify([value, option.id]),
              pageName,
            }),
          );
          onSelected(option);
        }}
        className={`${s.optionLink} ${
          option.id === value ? s.optionLinkActive : ''
        }`}
      >
        {option.label}
      </TextButton>
    </Cell>
  );

  return (
    <Box dataHook={dataHooks.currencySelector}>
      <Popover
        shown={shown}
        showArrow={popoverProps.showArrow}
        appendTo="parent"
        placement={popoverProps.placement}
        zIndex={5000}
        dataHook="blackpopover-sort-menu"
        fixed={false}
        minWidth={370}
        moveBy={popoverProps.moveBy}
        onClickOutside={() => setShown(false)}
        disableClickOutsideWhenClosed={true}
        id={popoverIds.currencyPopover}
        onKeyDown={(e: any) => {
          if (e.keyCode === 13) {
            setShown(!shown);
          }
        }}
        onClick={() => {
          if (popoverProps.enableClickTrigger) {
            setShown(!shown);
          }
        }}
        onMouseEnter={() => {
          if (popoverProps.enableHoverTrigger) {
            setShown(true);
          }
        }}
        onMouseLeave={() => {
          if (popoverProps.enableHoverTrigger) {
            setShown(false);
          }
        }}
      >
        <Popover.Element>
          <Box paddingLeft="SP2">
            <TextButton
              className={s[popoverProps.className]}
              skin="dark"
              size="medium"
              weight={popoverProps.triggerElementWeight}
              suffixIcon={
                popoverProps.withSuffixIcon ? <ChevronDown /> : undefined
              }
              dataHook="black-sort-popover-trigger"
            >
              {selection ? `${selection?.symbol} ${selection?.id}` : ''}
            </TextButton>
          </Box>
        </Popover.Element>
        <Popover.Content>
          <div
            style={{
              padding: '30px 6px 30px 18px',
              overflow: 'auto',
              maxHeight: '540px',
            }}
            onKeyDown={(e: any) => {
              if (e.keyCode === 27) {
                e.stopPropagation();
                setShown(false);
                (
                  document.querySelector(
                    `#${popoverIds.currencyPopover} button`,
                  ) as HTMLElement
                )?.focus();
              }
            }}
          >
            <Layout
              className={s.currencySelector}
              gap="SP0"
              alignItems="center"
              justifyItems="left"
            >
              {options?.map((option) => (
                <OptionCell option={option} key={option.id} />
              ))}
            </Layout>
          </div>
        </Popover.Content>
      </Popover>
    </Box>
  );
};

export default CurrencySelector;
